import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import BackButton from '../../components/common/BackButton';
import { usePermissions } from '../../hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import whiteLabelApi from '../../services/api/white-label.api';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';

interface WhiteLabelConfig {
  logo_url?: string;
  custom_name?: string;
  custom_url?: string;
}

export default function WhiteLabelPage() {
  const { t } = useTranslation(['whiteLabel', 'common']);
  const { canManageWhiteLabel } = usePermissions();
  const navigate = useNavigate();
  const { reload } = useWhiteLabel();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [config, setConfig] = useState<WhiteLabelConfig>({
    logo_url: '',
    custom_name: '',
    custom_url: '',
  });
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  // Check permission
  useEffect(() => {
    if (!canManageWhiteLabel) {
      navigate('/settings');
    }
  }, [canManageWhiteLabel, navigate]);

  // Load white label config
  useEffect(() => {
    loadConfig();
  }, []);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      console.log('[WhiteLabelPage] Loading config for settings page...');
      const response = await whiteLabelApi.getConfig(true); // Pass true for settings page
      console.log('[WhiteLabelPage] Config response:', response);
      if (response.success && response.data) {
        console.log('[WhiteLabelPage] Setting config:', response.data);
        setConfig(response.data);
      }
    } catch (error) {
      console.error('[WhiteLabelPage] Error loading white label config:', error);
      toast.error(t('whiteLabel:messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoFileChange = async (file: File) => {
    setSelectedLogoFile(file);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setLogoPreviewUrl(previewUrl);

    // Upload automatically
    setUploadingLogo(true);
    try {
      console.log('[WhiteLabelPage] Uploading logo...');
      const response = await whiteLabelApi.uploadLogo(file);
      console.log('[WhiteLabelPage] Upload response:', response);

      if (response.success) {
        toast.success(t('whiteLabel:messages.logoUploadSuccess'), {
          duration: 3000,
          icon: '✅',
        });

        // Clean up preview URL
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setLogoPreviewUrl(null);
        }
        setSelectedLogoFile(null);

        console.log('[WhiteLabelPage] Reloading configs...');
        // Reload config
        await loadConfig();
        // Reload global context
        await reload();
        console.log('[WhiteLabelPage] Configs reloaded');
      }
    } catch (error: any) {
      console.error('[WhiteLabelPage] Error uploading logo:', error);
      toast.error(t('whiteLabel:messages.logoUploadError'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await whiteLabelApi.updateConfig(config);

      if (response.success) {
        toast.success(t('whiteLabel:messages.saveSuccess'), {
          duration: 3000,
          icon: '✅',
        });
        // Reload global context
        await reload();
      }
    } catch (error: any) {
      console.error('Error saving config:', error);
      toast.error(t('whiteLabel:messages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = () => {
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
      setLogoPreviewUrl(null);
    }
    setSelectedLogoFile(null);
    setConfig({ ...config, logo_url: '' });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex h-96 items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">{t('whiteLabel:loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <BackButton />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('whiteLabel:title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {t('whiteLabel:subtitle')}
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="space-y-6">
          {/* Logo Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('whiteLabel:logo.title')}
            </label>

            {/* Current Logo */}
            {config.logo_url && !logoPreviewUrl && (
              <div className="mb-4 flex items-center space-x-4 p-4 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                <img
                  src={config.logo_url}
                  alt="Current logo"
                  className="h-16 w-auto rounded border border-gray-300 dark:border-gray-700 bg-white p-2"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('whiteLabel:logo.current')}</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('whiteLabel:logo.replace')}</p>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                >
                  {t('whiteLabel:logo.remove')}
                </button>
              </div>
            )}

            {/* Preview of Selected File */}
            {logoPreviewUrl && (
              <div className="mb-4 p-4 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-lg bg-indigo-50 dark:bg-indigo-900/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <img
                      src={logoPreviewUrl}
                      alt="Preview"
                      className="h-16 w-auto rounded border border-indigo-300 dark:border-indigo-600 bg-white p-2"
                    />
                    <div>
                      <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">{t('whiteLabel:logo.preview')}</span>
                      <p className="text-xs text-indigo-600 dark:text-indigo-400">{selectedLogoFile?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedLogoFile && `${(selectedLogoFile.size / 1024).toFixed(1)} KB`}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (logoPreviewUrl) {
                        URL.revokeObjectURL(logoPreviewUrl);
                      }
                      setLogoPreviewUrl(null);
                      setSelectedLogoFile(null);
                    }}
                    className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="file"
                accept="image/*"
                disabled={uploadingLogo}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleLogoFileChange(file);
                  }
                }}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
              />
              {uploadingLogo && (
                <div className="flex items-center space-x-2 text-indigo-600 dark:text-indigo-400">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm font-medium">{t('whiteLabel:logo.uploading')}</span>
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('whiteLabel:logo.recommendation')}
            </p>
          </div>

          {/* Platform Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('whiteLabel:name.title')}
            </label>
            <input
              type="text"
              value={config.custom_name || ''}
              onChange={(e) => setConfig({ ...config, custom_name: e.target.value })}
              placeholder={t('whiteLabel:name.placeholder')}
              disabled={saving}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('whiteLabel:name.help')}
            </p>
          </div>

          {/* Custom URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('whiteLabel:domain.title')}
            </label>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">https://</span>
              <input
                type="text"
                value={config.custom_url || ''}
                onChange={(e) => setConfig({ ...config, custom_url: e.target.value })}
                placeholder={t('whiteLabel:domain.placeholder')}
                disabled={saving}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white disabled:opacity-50"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('whiteLabel:domain.help')}
            </p>

            {/* DNS Configuration Instructions */}
            {config.custom_url && (
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">
                  {t('whiteLabel:domain.instructions')}
                </h4>

                <div className="space-y-3">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>1.</strong> {t('whiteLabel:domain.step1')}
                  </p>

                  <div>
                    <p className="text-xs text-blue-800 dark:text-blue-200 mb-2">
                      <strong>2.</strong> {t('whiteLabel:domain.step2')}
                    </p>
                    <div className="bg-white dark:bg-gray-900 rounded border border-blue-300 dark:border-blue-700 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-blue-100 dark:bg-blue-900/30">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-blue-900 dark:text-blue-100">
                              {t('whiteLabel:domain.record')}
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-blue-900 dark:text-blue-100">
                              {t('whiteLabel:domain.hostname')}
                            </th>
                            <th className="px-3 py-2 text-left font-semibold text-blue-900 dark:text-blue-100">
                              {t('whiteLabel:domain.target')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-t border-blue-200 dark:border-blue-800">
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-mono">CNAME</td>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-mono">{config.custom_url}</td>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-mono">admin-flux.kubmix.com</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>3.</strong> {t('whiteLabel:domain.step3')}
                  </p>

                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>4.</strong> {t('whiteLabel:domain.step4')}{' '}
                    <a
                      href="mailto:contato@devskin.com"
                      className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      contato@devskin.com
                    </a>{' '}
                    {t('whiteLabel:domain.step4End')}
                  </p>

                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>5.</strong> {t('whiteLabel:domain.step5')}{' '}
                    <span className="font-mono text-blue-600 dark:text-blue-400">https://{config.custom_url}</span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Preview Section */}
          <div className="mt-8 p-6 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">{t('whiteLabel:preview.title')}</h3>
            <div className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              {(config.logo_url || logoPreviewUrl) && (
                <img
                  src={logoPreviewUrl || config.logo_url}
                  alt="Logo preview"
                  className="h-10 w-auto"
                />
              )}
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {config.custom_name || 'Kubmix Flux'}
              </span>
            </div>
            {config.custom_url && (
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                {t('whiteLabel:preview.url')}: <span className="font-mono text-indigo-600 dark:text-indigo-400">https://{config.custom_url}</span>
              </p>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center space-x-2 transition-colors"
            >
              {saving && (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span>{saving ? t('whiteLabel:saving') : t('whiteLabel:save')}</span>
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
